import { useEffect, useRef } from 'react';
import './TopoBackground.css';

/**
 * The topographic-contour page ground.
 *
 * A faithful React port of the canvas generator on prqpartners.com: a value-noise
 * height field (3-octave fbm, time as the third dimension) rendered as contour
 * lines via marching squares, with the terrain drifting slowly and a soft "peak"
 * rising under the pointer. It is the brand's signature, so it is reproduced
 * rather than approximated.
 *
 * Adapted from the site's hero background to sit behind the whole app: the canvas
 * is a fixed, non-interactive ground covering the viewport, and the pointer is
 * tracked on the window instead of a hero element.
 *
 * The calibration constants are the site's, unchanged, except ALPHA — dialled
 * down, because here the contours sit behind a form rather than large display
 * text and must never compete with it.
 */

const CFG = {
  LEVELS: 12,
  LEVEL_START: 0.2,
  LEVEL_STEP: 0.028,
  INDEX_EVERY: 5,
  DRIFT: 0.0006,
  NOISE_SCALE: 2.4,
  BUMP: 0.3,
  BUMP_RADIUS: 0.2,
  FOLLOW: 0.04,
  IDLE_MS: 3000,
  // Site uses 0.15 / 0.25; softened for a form backdrop.
  ALPHA_MINOR: 0.09,
  ALPHA_INDEX: 0.16,
  WIDTH_MINOR: 0.75,
  WIDTH_INDEX: 1.25,
  CELL_DESKTOP: 8,
  CELL_MOBILE: 5,
  INK: '50,50,50',
} as const;

export function TopoBackground() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    let W = 0;
    let H = 0;
    let cell: number = CFG.CELL_DESKTOP;
    let nx = 0;
    let ny = 0;
    let field = new Float32Array(0);

    const levels: number[] = [];
    for (let k = 0; k < CFG.LEVELS; k++) levels.push(CFG.LEVEL_START + k * CFG.LEVEL_STEP);

    function resize() {
      W = window.innerWidth;
      H = window.innerHeight;
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      cell = W < 768 ? CFG.CELL_MOBILE : CFG.CELL_DESKTOP;
      canvas!.width = Math.round(W * dpr);
      canvas!.height = Math.round(H * dpr);
      ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
      nx = Math.ceil(W / cell) + 1;
      ny = Math.ceil(H / cell) + 1;
      field = new Float32Array(nx * ny);
    }

    // --- value noise, 3 octaves, time as the third dimension ---
    const fract = (x: number) => x - Math.floor(x);
    const hash3 = (i: number, j: number, k: number) =>
      fract(Math.sin(i * 127.1 + j * 311.7 + k * 74.7) * 43758.5453);
    const sm = (v: number) => v * v * (3 - 2 * v);
    function n3(x: number, y: number, z: number) {
      const xi = Math.floor(x);
      const yi = Math.floor(y);
      const zi = Math.floor(z);
      const u = sm(x - xi);
      const v = sm(y - yi);
      const w = sm(z - zi);
      const c = (a: number, b: number, d: number) => hash3(xi + a, yi + b, zi + d);
      const x00 = c(0, 0, 0) + (c(1, 0, 0) - c(0, 0, 0)) * u;
      const x10 = c(0, 1, 0) + (c(1, 1, 0) - c(0, 1, 0)) * u;
      const x01 = c(0, 0, 1) + (c(1, 0, 1) - c(0, 0, 1)) * u;
      const x11 = c(0, 1, 1) + (c(1, 1, 1) - c(0, 1, 1)) * u;
      const y0 = x00 + (x10 - x00) * v;
      const y1 = x01 + (x11 - x01) * v;
      return y0 + (y1 - y0) * w;
    }
    const fbm = (x: number, y: number, z: number) =>
      n3(x, y, z) * 0.55 + n3(x * 2.1, y * 2.1, z * 1.6) * 0.3 + n3(x * 4.3, y * 4.3, z * 2.2) * 0.15;

    // --- pointer with inertia + idle wander ---
    let t = Math.random() * 100;
    let mx = 0;
    let my = 0;
    let tx = 0;
    let ty = 0;
    let lastMove = 0;

    const onPointerMove = (e: PointerEvent) => {
      tx = e.clientX;
      ty = e.clientY;
      lastMove = performance.now();
    };

    const e0: [number, number] = [0, 0];
    const e1: [number, number] = [0, 0];
    const e2: [number, number] = [0, 0];
    const e3: [number, number] = [0, 0];
    function lerpPt(
      out: [number, number],
      x1: number,
      y1: number,
      x2: number,
      y2: number,
      v1: number,
      v2: number,
      l: number,
    ) {
      const d = v2 - v1;
      const s = Math.abs(d) < 1e-6 ? 0.5 : (l - v1) / d;
      out[0] = x1 + (x2 - x1) * s;
      out[1] = y1 + (y2 - y1) * s;
    }
    const seg = (a: [number, number], b: [number, number]) => {
      ctx!.moveTo(a[0], a[1]);
      ctx!.lineTo(b[0], b[1]);
    };

    function frame(advance: boolean) {
      if (advance) t += CFG.DRIFT;
      if (advance && performance.now() - lastMove > CFG.IDLE_MS) {
        tx = W / 2 + W * 0.32 * Math.sin(t * 0.9);
        ty = H / 2 + H * 0.3 * Math.sin(t * 1.4 + 1.3);
      }
      mx += (tx - mx) * CFG.FOLLOW;
      my += (ty - my) * CFG.FOLLOW;

      const freq = CFG.NOISE_SCALE / Math.min(W, H);
      const sig = CFG.BUMP_RADIUS * Math.min(W, H);
      const s2 = 2 * sig * sig;

      for (let j = 0; j < ny; j++) {
        for (let i = 0; i < nx; i++) {
          const px = i * cell;
          const py = j * cell;
          let v = fbm(px * freq, py * freq, t);
          const dx = px - mx;
          const dy = py - my;
          v += CFG.BUMP * Math.exp(-(dx * dx + dy * dy) / s2);
          field[j * nx + i] = v;
        }
      }

      ctx!.clearRect(0, 0, W, H);
      ctx!.lineJoin = 'round';
      ctx!.lineCap = 'round';

      for (let k = 0; k < levels.length; k++) {
        const l = levels[k]!;
        const isIdx = k % CFG.INDEX_EVERY === 0;
        ctx!.beginPath();
        for (let j2 = 0; j2 < ny - 1; j2++) {
          for (let i2 = 0; i2 < nx - 1; i2++) {
            const x0 = i2 * cell;
            const y0 = j2 * cell;
            const x1 = x0 + cell;
            const y1 = y0 + cell;
            const v0 = field[j2 * nx + i2]!;
            const v1 = field[j2 * nx + i2 + 1]!;
            const v2 = field[(j2 + 1) * nx + i2 + 1]!;
            const v3 = field[(j2 + 1) * nx + i2]!;
            const c2 = (v0 > l ? 8 : 0) | (v1 > l ? 4 : 0) | (v2 > l ? 2 : 0) | (v3 > l ? 1 : 0);
            if (c2 === 0 || c2 === 15) continue;
            lerpPt(e0, x0, y0, x1, y0, v0, v1, l);
            lerpPt(e1, x1, y0, x1, y1, v1, v2, l);
            lerpPt(e2, x0, y1, x1, y1, v3, v2, l);
            lerpPt(e3, x0, y0, x0, y1, v0, v3, l);
            switch (c2) {
              case 1: seg(e2, e3); break;
              case 2: seg(e1, e2); break;
              case 3: seg(e1, e3); break;
              case 4: seg(e0, e1); break;
              case 5: seg(e0, e3); seg(e1, e2); break;
              case 6: seg(e0, e2); break;
              case 7: seg(e0, e3); break;
              case 8: seg(e0, e3); break;
              case 9: seg(e0, e2); break;
              case 10: seg(e0, e1); seg(e2, e3); break;
              case 11: seg(e0, e1); break;
              case 12: seg(e1, e3); break;
              case 13: seg(e1, e2); break;
              case 14: seg(e2, e3); break;
            }
          }
        }
        ctx!.strokeStyle = `rgba(${CFG.INK},${isIdx ? CFG.ALPHA_INDEX : CFG.ALPHA_MINOR})`;
        ctx!.lineWidth = isIdx ? CFG.WIDTH_INDEX : CFG.WIDTH_MINOR;
        ctx!.stroke();
      }
    }

    // --- lifecycle: reduced motion, tab visibility, resize ---
    let running = false;
    let raf = 0;
    const loop = () => {
      if (!running) return;
      frame(true);
      raf = requestAnimationFrame(loop);
    };
    const start = () => {
      if (running || motionQuery.matches || document.hidden) return;
      running = true;
      raf = requestAnimationFrame(loop);
    };
    const stop = () => {
      running = false;
      cancelAnimationFrame(raf);
    };

    const onVisibility = () => {
      if (document.hidden) stop();
      else start();
    };
    const onMotionChange = () => {
      stop();
      frame(false); // settle to the static state
    };

    let rTimer: number | undefined;
    const onResize = () => {
      window.clearTimeout(rTimer);
      rTimer = window.setTimeout(() => {
        resize();
        frame(false);
        if (!running) start();
      }, 150);
    };

    window.addEventListener('pointermove', onPointerMove, { passive: true });
    window.addEventListener('resize', onResize);
    document.addEventListener('visibilitychange', onVisibility);
    motionQuery.addEventListener('change', onMotionChange);

    resize();
    tx = W / 2;
    ty = H / 2;
    mx = tx;
    my = ty;
    frame(false); // static first paint; also the permanent reduced-motion state
    start();

    return () => {
      stop();
      window.clearTimeout(rTimer);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', onVisibility);
      motionQuery.removeEventListener('change', onMotionChange);
    };
  }, []);

  return <canvas ref={canvasRef} className="topo-bg" aria-hidden="true" />;
}
